import type { Attachment, Message } from "@catmail/shared";
import { Paperclip } from "lucide-react";

import { Dialog, EmptyState } from "@/components";

import { filesOf } from "../../lib/roomView";
import { AttachmentCard } from "../AttachmentCard";

/**
 * 이 방에서 주고받은 파일 모아 보기.
 *
 * 헤더의 클립과 프로필의 "받은 파일"이 여기로 온다. 둘 다 원래는 눌러도 아무
 * 일이 없었다. 개수까지 세어 보여주면서 열리지 않는 것이 제일 나쁘다.
 *
 * 파일 한 줄은 대화 안에서 쓰는 것과 **같은 컴포넌트**다. 같은 파일이 자리에
 * 따라 다르게 생기면 같은 것인지 알아보기 어렵다.
 *
 * 목록은 이미 불러온 메시지에서 뽑는다. 위로 더 올라가지 않은 옛 메시지의
 * 파일은 아직 안 보인다 — 그래서 "지금까지 불러온 대화 기준"이라고 적는다.
 */

type RoomFilesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messages: Message[];
  onDownload: (attachment: Attachment) => void;
};

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

export function RoomFilesDialog({
  open,
  onOpenChange,
  messages,
  onDownload,
}: RoomFilesDialogProps) {
  // 본문에 박힌 그림(cid:)은 뺀다. 안 그러면 광고 메일의 로고와 배너가
  // "주고받은 파일"을 가득 채운다.
  const files = messages.flatMap((message) =>
    filesOf(message).map((attachment) => ({ attachment, message })),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="sm">
        <div className="border-line-soft flex flex-col gap-1 border-b px-6 pt-5.5 pb-4">
          <Dialog.Title>주고받은 파일</Dialog.Title>
          <Dialog.Close />
          <Dialog.Description>
            {files.length > 0
              ? `${files.length}개 · 지금까지 불러온 대화 기준`
              : "아직 주고받은 파일이 없어요"}
          </Dialog.Description>
        </div>

        {/* 비었을 때 빈 상자만 남기면 창이 덜 만들어진 것처럼 보인다 */}
        {files.length === 0 ? (
          <EmptyState
            icon={<Paperclip />}
            title="아직 주고받은 파일이 없어요"
            description="이 대화에서 파일을 주고받으면 여기 모여요."
          />
        ) : (
        <ul className="flex max-h-100 flex-col gap-2 overflow-y-auto px-6 py-4">
          {files.map(({ attachment, message }) => (
            <li key={attachment.id} className="flex flex-col gap-1">
              <AttachmentCard
                attachment={attachment}
                onOpen={onDownload}
                className="w-full"
              />
              <span className="text-muted-3 pl-1 text-[11px]">
                {formatDate(message.sentAt)}
              </span>
            </li>
          ))}
        </ul>
        )}
      </Dialog.Content>
    </Dialog>
  );
}
